<template>
  <div class="sp-finder-filter-bar" :class="classModifiers">
    <FinderFilterMenu
      v-if="menuItems.length > 0"
      class="sp-finder-filter-bar__menu"
      part="menu"
      :menu-items="menuItems"
      :icon="menuIcon"
      :applied-count="appliedCount"
      :resettable-count="resettableCount"
      @select="openFilter"
      @before-reveal="closeLocalFilters"
      @clear="clearFilter"
      @clear-all="clearAllFilters"
    />

    <div class="sp-finder-filter-bar__track">
      <div ref="scroller" class="sp-finder-filter-bar__scroller">
        <div class="sp-finder-filter-bar__row">
          <span ref="rowStart" class="sp-finder-filter-bar__sentinel --start" aria-hidden="true"></span>
          <div class="sp-finder-filter-bar__chips"><slot ref="filters"></slot></div>
          <span ref="rowEnd" class="sp-finder-filter-bar__sentinel --end" aria-hidden="true"></span>
        </div>
      </div>

      <span
        v-if="hasOverflowStart"
        class="sp-finder-filter-bar__fade --start"
        part="overflow-fade"
        aria-hidden="true"
      ></span>
      <template v-if="showsOverflowStart">
        <sp-button
          class="sp-finder-filter-bar__arrow --start"
          part="overflow-button"
          variant="text"
          :aria-label="startArrowLabel"
          :data-testid="TestId.finder.filterBarScrollStart"
          @click="scrollTowardsStart"
        >
          <sp-icon :name="startArrowIcon"></sp-icon>
        </sp-button>
      </template>

      <template v-if="showsOverflowEnd">
        <span class="sp-finder-filter-bar__fade --end" part="overflow-fade" aria-hidden="true"></span>
        <sp-button
          class="sp-finder-filter-bar__arrow --end"
          part="overflow-button"
          variant="text"
          :aria-label="endArrowLabel"
          :data-testid="TestId.finder.filterBarScrollEnd"
          @click="scrollTowardsEnd"
        >
          <sp-icon :name="endArrowIcon"></sp-icon>
        </sp-button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, ref, toRef, useTemplateRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useHostElement } from "#ce/composables/useHostElement";
import { useExpose } from "#ce/composables/useExpose";
import {
  FINDER_FILTER_CONNECT_EVENT,
  FINDER_FILTER_STATE_EVENT,
  FINDER_FILTER_TAG,
} from "#ce/components/SpFinder/engine/finder-filter-protocol";
import { toBoolean } from "#ce/utils/props";
import { props as barProps } from "./props";
import { TestId } from "#/test-ids";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { useFinderFilterMenu } from "./composables/useFinderFilterMenu";
import { useFinderFilterOrder } from "./composables/useFinderFilterOrder";
import { useFinderFilterOverflow } from "./composables/useFinderFilterOverflow";
import { useFinderFilterRegistry } from "./composables/useFinderFilterRegistry";
import { useFinderContributions } from "./composables/useFinderContributions";
import FinderFilterMenu from "./components/FinderFilterMenu.ce.vue";

const props = defineProps(barProps);

const host = useHostElement();
const scroller = useTemplateRef("scroller");
const rowStart = useTemplateRef("rowStart");
const rowEnd = useTemplateRef("rowEnd");
const slot = useTemplateRef("filters");

const { registry, register, applyState, prune, clear } = useFinderFilterRegistry(slot);
const { contributions, connectContribution } = useFinderContributions();
const { exposeMethods } = useExpose();
exposeMethods({
  connectContribution,
});

useFinderFilterOrder(registry, scroller, toRef(props, "activeOrder"));

const {
  menuItems,
  appliedCount,
  resettableCount,
  open: openFilter,
  clear: clearFilter,
  clearAll: clearAllFilters,
  settleRegistration,
  closeLocalFilters,
} = useFinderFilterMenu(registry, contributions, prune, () => toBoolean(props.includeWithheld), unregisteredFilters);

// A slotted filter still upgrading has not announced itself, so the registry does not know it yet.
function unregisteredFilters() {
  const assigned = slot.value?.assignedElements({ flatten: true }) ?? [];
  const filters = assigned.flatMap(filtersWithin);

  return filters.filter((element) => !registry.value.has(element));
}

function filtersWithin(element) {
  if (element.localName === FINDER_FILTER_TAG) {
    return [element];
  }

  return [...element.querySelectorAll(FINDER_FILTER_TAG)];
}

const { hasOverflowStart, hasOverflowEnd, scrollTowardsStart, scrollTowardsEnd } = useFinderFilterOverflow(
  scroller,
  rowStart,
  rowEnd,
);

// An arrow points the way the row travels, which is the opposite physical way in a right-to-left
// row. The direction is read from the host, so an inherited `dir` counts as much as its own.
const isRtl = ref(false);

function readDirection() {
  isRtl.value = host ? getComputedStyle(host).direction === "rtl" : false;
}

onMounted(readDirection);
useMutationObserver(host?.ownerDocument.documentElement, readDirection, {
  attributes: true,
  attributeFilter: ["dir", "lang"],
  subtree: true,
});

const startArrowIcon = computed(() => (isRtl.value ? "arrow-right-filled" : "arrow-left-filled"));
const endArrowIcon = computed(() => (isRtl.value ? "arrow-left-filled" : "arrow-right-filled"));

// The labels name the direction the control moves the row, which is the physical one the glyph
// already points at. A right-to-left row swaps both together.
const startArrowLabel = computed(() =>
  tAccessibleName(isRtl.value ? "filter_bar_scroll_right" : "filter_bar_scroll_left"),
);
const endArrowLabel = computed(() =>
  tAccessibleName(isRtl.value ? "filter_bar_scroll_left" : "filter_bar_scroll_right"),
);

// Below the sm breakpoint the arrows measure zero wide, so the row is swiped instead.
const { isGtSmScreen } = useBreakpoints();
const showsOverflowStart = computed(() => isGtSmScreen.value && hasOverflowStart.value);
const showsOverflowEnd = computed(() => isGtSmScreen.value && hasOverflowEnd.value);

// The inline bleed, its scroll padding and its pointer transparency belong to the arrows and fades,
// so they arrive with them.
const classModifiers = computed(() => {
  const modifiers = {
    "--is-wide": isGtSmScreen.value,
  };

  return modifiers;
});

watch([showsOverflowStart, showsOverflowEnd], readDirection);

// Both events bubble up from a slotted filter's host, so `event.target` is the filter.
function handleFilterConnect(event) {
  const [payload] = event.detail;
  register(event.target, payload);
  settleRegistration(event.target);
}

function handleFilterState(event) {
  const [state] = event.detail;
  applyState(event.target, state);
}

useEventListener(host, FINDER_FILTER_CONNECT_EVENT, handleFilterConnect);
useEventListener(host, FINDER_FILTER_STATE_EVENT, handleFilterState);

useEventListener(slot, "slotchange", prune);

// Removing a filter inside an assigned wrapper does not fire `slotchange`, so subtree removals
// also prune the registry. Detaching and reinserting the bar does not mutate its subtree.
useMutationObserver(host, prune, {
  childList: true,
  subtree: true,
});

onBeforeUnmount(clear);
</script>

<style>
:host {
  display: block;

  /* A scroll viewport clips on every side, so it extends this far past the chips and a selected
     chip's shadow has room to fade. Sized for the widest shadow in use: 6px offset, 24px blur. */
  --shadow-bleed: var(--sp-comp-finder-filter-bar-shadow-bleed, var(--sp-ref-scale-32, 2rem));
  --row-padding-block: var(--sp-comp-finder-filter-bar-row-padding-block, var(--sp-ref-scale-6, 0.375rem));
  --arrow-size: var(--sp-comp-finder-filter-bar-arrow-size, var(--sp-ref-scale-32, 2rem));
  /* An arrow's outer box: its content size plus 5px padding and a 1px border on each side. The
     host carries the border, so the control itself is sized to everything inside it. */
  --arrow-radius: var(--sp-comp-finder-filter-bar-arrow-border-radius, 12px);
  --arrow-box: calc(var(--arrow-size) + 12px);
  --arrow-inner: calc(var(--arrow-size) + 10px);
  --edge-fade-width: var(--sp-comp-finder-filter-bar-fade-width, var(--sp-ref-scale-24, 1.5rem));
  /* The edge overlay is opaque from the viewport edge through the arrow, then fades out past it. */
  --edge-opaque-width: calc(var(--shadow-bleed) + var(--arrow-box));
  --edge-width: calc(var(--edge-opaque-width) + var(--edge-fade-width));
  --fade-color: var(--sp-comp-finder-filter-bar-fade-color, var(--sp-sys-color-surface, #fff));
  /* An overlay fades away from the edge it sits on, so its gradient follows the physical direction
     that edge resolves to. */
  --fade-direction-start: to right;
  --fade-direction-end: to left;
}

:host(:dir(rtl)) {
  --fade-direction-start: to left;
  --fade-direction-end: to right;
}
</style>

<style lang="scss" scoped>
.sp-finder-filter-bar {
  display: flex;
  align-items: center;
  gap: var(--sp-comp-finder-filter-bar-menu-gap, var(--sp-ref-scale-8));
  clip-path: inset(calc(var(--shadow-bleed) * -1) -16px);
  overflow-y: var(--sp-comp-finder-filter-bar-overflow-y, visible);
  padding-block-end: var(--sp-comp-finder-filter-bar-padding-block-end, 0);

  // The mobile rail has no arrow: cover the bleed beneath the pinned menu, then fade
  // into the chips so the scroll viewport never leaves a hard edge beside the trigger.
  --edge-opaque-width: var(--shadow-bleed);
  --edge-width: calc(var(--edge-opaque-width) + var(--edge-fade-width));

  /* The start fade begins one bleed before the track, inside the trigger's trailing edge, so the
     menu outranks it. The rank matches `sp-popup`'s fallback, so an open panel is not confined
     below it where the top layer is off. */
  &__menu {
    flex: 0 0 auto;
    z-index: 10;
  }

  /* The positioning context for the fade and its arrow, which overlay the scrolled row rather than
     taking width from it, so a chip scrolls underneath both instead of stopping short of them. The
     menu sits outside it and keeps its own width.

     A zero basis: the scroller inside is wider than the rail by the bleed, so a content basis would
     size the track to the whole row rather than to the space left beside the menu. */
  &__track {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    margin-inline-end: calc(var(--sp-comp-finder-filter-bar-mobile-end-bleed, var(--sp-ref-scale-16)) * -1);
  }

  /* The viewport reaches one bleed above and below the row and the row pads itself back, so a
     selected chip keeps its position while its shadow renders instead of meeting the clip edge.
     The inline half of the bleed belongs to the arrows and fades, so it arrives with them. */
  &__scroller {
    overflow-x: auto;
    overflow-y: hidden;
    margin-block: calc(var(--shadow-bleed) * -1);
    scroll-behavior: smooth;
    scrollbar-width: none;
    margin-inline-start: calc(var(--shadow-bleed) * -1);
    padding-inline-start: var(--shadow-bleed);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    width: max-content;
    padding-block: calc(var(--row-padding-block) + var(--shadow-bleed));
  }

  /* The chips own the gap, so the sentinels beside them take none: level with the row they would
     each add one, insetting the first chip and leaving the same again as dead space after the last.
     It is also the box that takes pointer input, so a wheel over the space between two chips
     scrolls the row rather than falling through to the bar. */
  &__chips {
    display: flex;
    align-items: center;
    gap: var(--sp-comp-finder-filter-bar-gap, var(--sp-ref-scale-10));
  }

  /* Above the sm breakpoint the rail carries arrows and fades, and the inline bleed they overlay.
     `scroll-padding-inline` reserves that overlay, so a chip scrolled into view by
     `scrollIntoView({ inline: "nearest" })` lands clear of the arrow rather than beneath it.

     The inline bleed is an invisible box reaching over whatever sits beside the rail, so only the
     row takes pointer input. Below the breakpoint the scroller keeps its own pointer events,
     because Mobile Safari will not pan one that has none — and it has no inline bleed to widen the
     page with. */
  &.--is-wide {
    --edge-opaque-width: calc(var(--shadow-bleed) + var(--arrow-box));

    clip-path: none;
    gap: var(--sp-comp-finder-filter-bar-menu-gap, var(--sp-comp-finder-filter-bar-gap, var(--sp-ref-scale-10)));

    .sp-finder-filter-bar__track {
      margin-inline-end: 0;
    }

    .sp-finder-filter-bar__scroller {
      width: calc(100% + var(--shadow-bleed) * 2);
      margin-inline: calc(var(--shadow-bleed) * -1);
      padding-inline-start: 0;
      scroll-padding-inline: var(--edge-width);
      pointer-events: none;
    }

    .sp-finder-filter-bar__row {
      padding-inline: var(--shadow-bleed);
    }

    /* The bleed is an invisible box over whatever sits beside and above the rail, so the chips take
       pointer input and the space around them does not. Below the breakpoint there is no inline
       bleed and the scroller keeps its own events, because Mobile Safari will not pan one without. */
    .sp-finder-filter-bar__chips {
      padding-inline: var(--sp-comp-finder-filter-bar-chip-inset, 3px);
      pointer-events: auto;
    }

    .sp-finder-filter-bar__sentinel.--end {
      margin-inline: var(--shadow-bleed) calc(var(--shadow-bleed) * -1);
    }
  }

  /* The chips are ranked by `order` inside their own box, so these sit outside it in document
     order and need no rank of their own.

     The scroll viewport reaches one bleed past the rail on the start side at every width, and on
     both sides above the sm breakpoint. On each such side the sentinel sits one bleed outside the
     chips, so it leaves the viewport as soon as the chip beside it passes the rail's edge. The
     paired negative margin keeps the row's width the same, so the offset costs no scrollable
     distance. */
  &__sentinel {
    flex: 0 0 auto;
    width: 0;

    &.--start {
      margin-inline: calc(var(--shadow-bleed) * -1) var(--shadow-bleed);
    }
  }

  /* Opaque from the rail's edge through the whole arrow, then fading past it, so a chip or its
     shadow never shows beneath or beside the control. The lower extent can match an owning
     header's bottom padding, so its background ends at the header boundary. */
  &__fade {
    position: absolute;
    top: calc(var(--shadow-bleed) * -1);
    z-index: 3;
    height: calc(100% + var(--shadow-bleed) + var(--sp-comp-finder-filter-bar-fade-bottom-bleed, var(--shadow-bleed)));
    width: var(--edge-width);
    pointer-events: none;

    /* Inset by the bleed, so the overlay starts at the scroller's own edge: the chips that scroll
       into the bleed are covered rather than left showing outside the rail. That is also why the
       opaque run is the bleed plus the arrow's box — it reaches from there through the control. */
    &.--start {
      inset-inline-start: calc(var(--shadow-bleed) * -1);
      background: linear-gradient(var(--fade-direction-start), var(--fade-color) var(--edge-opaque-width), transparent);
    }

    &.--end {
      inset-inline-end: calc(var(--shadow-bleed) * -1);
      background: linear-gradient(var(--fade-direction-end), var(--fade-color) var(--edge-opaque-width), transparent);
    }
  }

  /* `--variant-text` paints nothing, so the control's surface is the host's own. The control is the
     44px square the rail uses — a 32px content box inside 5px of padding and a 1px border. */
  /* `arrow-*-filled` centres its 16px glyph in a 32 viewBox, so the icon box is twice the size the
     glyph draws at. */
  &__arrow {
    /* `--sp-comp-button-size` is the height; the min-* pair only bounds it. */
    /* The inner control fills the arrow's shape, inset by the border the host draws, so the hover
       tint stops at the rounded edge instead of showing square corners outside it — and a client's
       button radius cannot round a control the rail has already shaped. */
    --sp-comp-button-border-radius: calc(var(--arrow-radius) - 1px);
    --sp-comp-button-size: var(--arrow-inner);
    --sp-comp-button-min-width: var(--arrow-inner);
    --sp-comp-button-min-height: var(--arrow-inner);
    --sp-comp-button-padding: 0;
    --sp-comp-icon-size: calc(var(--sp-comp-finder-filter-bar-arrow-icon-size, var(--sp-ref-scale-16, 1rem)) * 2);

    position: absolute;
    top: 50%;
    z-index: 4;
    transform: translateY(-50%);
    border: 1px solid var(--sp-comp-finder-filter-bar-arrow-border-color, #b8c2c7);
    border-radius: var(--arrow-radius);
    background: var(--sp-comp-finder-filter-bar-arrow-bg-color, #fff);
    box-shadow: var(--sp-comp-finder-filter-bar-arrow-shadow, 0 1px 3px 0 rgb(0 0 0 / 25%));

    &.--start {
      inset-inline-start: 0;
    }

    &.--end {
      inset-inline-end: 0;
    }
  }
}

::slotted(*) {
  flex: 0 0 auto;
  width: auto;
}
</style>
